import PocketBase, { ClientResponseError } from "pocketbase";
import { config } from "@/config";

/**
 * Zentraler PocketBase-Client. Einzige Stelle, an der die SDK direkt
 * instanziiert wird. Alles andere läuft über die Service-Schicht
 * (src/services/*), damit ein Backend-Wechsel später lokal bleibt.
 * Die Backend-URL kommt zentral aus src/config.ts.
 */
export const pb = new PocketBase(config.pocketbaseUrl);

// WICHTIG: Auto-Cancellation aus. Sonst brechen parallele Requests an dieselbe
// Collection (z. B. Themen je Fach) sich gegenseitig ab -> status 0.
pb.autoCancellation(false);

// Ohne Zeitlimit hängt ein Request bei schlechtem WLAN (Verbunden, aber kein
// Internet) im Browser mehrere Minuten, bevor er fehlschlägt – die Seite
// wirkt dann wie eingefroren statt einen Fehler/Offline-Zustand zu zeigen.
// 10 s reichen für jede normale Anfrage, danach lieber sichtbar fehlschlagen.
pb.beforeSend = (url, options) => {
  if (!options.signal) options.signal = AbortSignal.timeout(10_000);
  return { url, options };
};

/**
 * Datei-Upload per XHR statt SDK: `fetch()` (das die SDK verwendet) kennt in
 * keiner der hier unterstützten WebViews echten Upload-Fortschritt, `XMLHttpRequest`
 * schon (`upload.onprogress`) – für die Fortschrittsanzeige bei großen Dateien
 * (siehe TopicPage.ts) bleibt das der einzige verlässliche Weg. Baut denselben
 * Request wie `pb.collection(x).create()/update()` von Hand nach (Auth-Header,
 * Fehlerform), damit `pbErrorMessage()` unverändert funktioniert.
 */
export function uploadWithProgress<T>(
  path: string,
  method: "POST" | "PATCH",
  form: FormData,
  onProgress: (fraction: number) => void,
  timeoutMs: number
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = pb.buildURL(path);
    xhr.open(method, url);
    xhr.timeout = timeoutMs;
    if (pb.authStore.token) xhr.setRequestHeader("Authorization", pb.authStore.token);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let json: Record<string, unknown> = {};
      try {
        json = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        /* keine gültige JSON-Antwort */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(json as T);
      else reject(new ClientResponseError({ status: xhr.status, response: json, url }));
    };
    xhr.onerror = () => reject(new ClientResponseError({ status: 0, url }));
    xhr.ontimeout = () => reject(new ClientResponseError({ status: 0, url, isAbort: true }));
    xhr.send(form);
  });
}

/**
 * Datei-URL eines PocketBase-Records ermitteln. Kapselt API-Unterschiede
 * zwischen SDK-Versionen (files.getURL / files.getUrl / getFileUrl).
 */
export function fileUrl(
  record: Record<string, unknown>,
  filename: string,
  query?: Record<string, unknown>
): string {
  if (!filename) return "";
  const anyPb = pb as unknown as {
    files?: { getURL?: Function; getUrl?: Function };
    getFileUrl?: Function;
  };
  if (anyPb.files?.getURL) return anyPb.files.getURL(record, filename, query);
  if (anyPb.files?.getUrl) return anyPb.files.getUrl(record, filename, query);
  if (anyPb.getFileUrl) return anyPb.getFileUrl(record, filename, query);
  return "";
}

// Ohne passenden Eintrag in `thumbs` des Dateifelds liefert PocketBase einfach das Original.
export function thumbUrl(url: string): string {
  if (!url.startsWith(pb.baseURL) || !/\/api\/files\/[^?]+\.(jpe?g|png|webp|gif)(\?|$)/i.test(url) || /[?&]thumb=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}thumb=1280x0`;
}

/** Macht aus PocketBase-Fehlern eine kurze, lesbare deutsche Meldung. */
export function pbErrorMessage(err: unknown): string {
  const e = err as {
    message?: string;
    status?: number;
    aiHandled?: boolean;
    data?: { message?: string; data?: Record<string, { message?: string }> };
  };
  // Die KI-Schicht formuliert ihre Fehler bereits selbst und weiß dabei mehr
  // (Zeitüberschreitung vs. Server tot vs. Proxy fehlt). Nicht überschreiben.
  if (e?.aiHandled) return e.message ?? "KI-Anfrage fehlgeschlagen.";
  // Eigene Meldung für abgelaufene/ungültige Sitzungen: sonst sieht ein
  // gescheiterter Speicherversuch nach abgelaufenem Token aus wie ein
  // beliebiger Serverfehler – der Nutzer merkt nicht, DASS er sich neu
  // anmelden muss, und die Änderung ist weg, ohne dass er es versteht.
  if (e?.status === 401) {
    return "Sitzung abgelaufen – bitte neu anmelden. Diese Änderung wurde NICHT gespeichert.";
  }
  // Feldspezifische Validierungsfehler bevorzugen
  const fieldErrors = e?.data?.data;
  if (fieldErrors && typeof fieldErrors === "object") {
    const first = Object.values(fieldErrors)[0];
    if (first?.message) return first.message;
  }
  if (e?.data?.message) return e.data.message;
  if (e?.status === 0) {
    return (
      "Keine Verbindung zum Server. Ist PocketBase unter " +
      config.pocketbaseUrl +
      " erreichbar? (URL in src/config.ts)"
    );
  }
  return e?.message || "Unbekannter Fehler.";
}
